import React, {
  ComponentType,
  ForwardRefExoticComponent,
  ReactElement,
} from "react";
import {
  backgroundColorModeMapper,
  BackgroundColorWithMode,
} from "@kameleon-util/global-theme";

import { Box } from "theme-ui";
import {
  BlockTheme,
  BlockType,
  FCPrimaryBlock,
  FCPrimaryCollectionBlock,
  PrimaryBlockType,
  PrimaryCollectionBlockType,
  PrimaryBlockConfig,
} from "@kameleon-core/types";
import { ThemeableBlock } from "./themeable-block";

/** PrimaryBlock layout */
function primaryBlock<T extends PrimaryBlockType>(
  config: PrimaryBlockConfig,
  RenderComponent: ComponentType<T> | ForwardRefExoticComponent<T>
): FCPrimaryBlock<T> {
  const {
    secondaryBlockSize,
    tertiaryBlockSize,
    blockTheme,
    blockType,
    isInnerBlock,
  } = config;
  const blockTypeDefault = blockType ?? BlockType.Primary;

  const block: FCPrimaryBlock<T> = (props: T): ReactElement => {
    // Do not wrap the component in a themeable primary block theme whenever it is already part of another primaryCollectionBlock
    const propsAsAny = props as any;
    let themeMode = config.blockTheme;
    let backgroundColorWithMode: BackgroundColorWithMode | undefined;

    if (propsAsAny?.layoutProps?.backgroundColor) {
      backgroundColorWithMode = backgroundColorModeMapper(
        propsAsAny.layoutProps.backgroundColor
      );
      themeMode = backgroundColorWithMode.mode;
    }

    const blockTypeToClassNameMapping: { [name: string]: string } = {
      [BlockType[BlockType.Primary]]: "section-primary--block",
      [BlockType[BlockType.PrimaryCollection]]:
        "section-primary-collection--block",
    };
    const className =
      blockTypeToClassNameMapping[BlockType[blockTypeDefault]] ?? "";

    const textAlignment = propsAsAny.layoutProps?.textAlignment;

    return (
      <ThemeableBlock
        isInnerBlock={isInnerBlock}
        blockType={blockTypeDefault}
        blockTheme={themeMode}
        backgroundColorWithMode={backgroundColorWithMode}
        className={className}
      >
        <Box sx={{ textAlign: textAlignment }}>
          <RenderComponent {...props} />
        </Box>
      </ThemeableBlock>
    );
  };
  block.blockConfig = config;
  block.secondaryBlockSize = secondaryBlockSize;
  block.tertiaryBlockSize = tertiaryBlockSize;
  block.blockType = blockTypeDefault;
  block.blockTheme = blockTheme;
  return block;
}

/** PrimaryCollectionBlock layout */
function primaryCollectionBlock<T extends PrimaryCollectionBlockType>(
  config: PrimaryBlockConfig,
  RenderComponent: ComponentType<T>
): FCPrimaryCollectionBlock<T> {
  return primaryBlock(
    {
      ...config,
      blockType: BlockType.PrimaryCollection,
      blockTheme: BlockTheme.light,
    },
    RenderComponent
  );
}

export { primaryBlock, primaryCollectionBlock };
